import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import InvoiceItemsEditor from "@/components/InvoiceItemsEditor";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  Save,
  FileText,
  ShoppingCart,
  TrendingUp,
  Trash2,
  Receipt,
  Calculator,
  Building,
  User,
  Calendar,
  IndianRupee,
  Sparkles,
  CheckCircle,
} from "lucide-react";
import { deriveGstBreakdown } from "@/utils/gst";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const emptyForm = () => ({
  invoice_no: "",
  invoice_date: "",
  party_name: "",
  party_gst: "",
  party_address: "",
  items: [],
});

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const buildInvoicePayload = (invoiceType, form, companyGstNo) => {
  const isPurchase = invoiceType === "purchase";
  const extractedData = {
    invoice_no: form.invoice_no,
    invoice_date: form.invoice_date,
    items: form.items || [],
    supplier_name: isPurchase ? form.party_name : "",
    supplier_gst_no: isPurchase ? form.party_gst : companyGstNo,
    supplier_address: isPurchase ? form.party_address : "",
    buyer_name: isPurchase ? "" : form.party_name,
    buyer_gst_no: isPurchase ? companyGstNo : form.party_gst,
    buyer_address: isPurchase ? "" : form.party_address,
  };

  return {
    ...extractedData,
    ...deriveGstBreakdown({
      invoiceType,
      companyGstNo,
      extractedData,
    }),
  };
};

export default function ManualEntry() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("sales");
  const [companyType, setCompanyType] = useState("");
  const [companyGstNo, setCompanyGstNo] = useState("");
  const [salesForm, setSalesForm] = useState(emptyForm);
  const [purchaseForm, setPurchaseForm] = useState(emptyForm);
  const [salesMasterData, setSalesMasterData] = useState({ parties: [], items: [] });
  const [purchaseMasterData, setPurchaseMasterData] = useState({ parties: [], items: [] });

  const loadMasterData = async (invoiceType) => {
    const token = localStorage.getItem("token");
    const response = await axios.get(`${API}/invoices/master-data`, {
      params: { invoice_type: invoiceType },
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data || { parties: [], items: [] };
  };

  useEffect(() => {
    const loadPageData = async () => {
      try {
        const token = localStorage.getItem("token");
        const [settingsResponse, purchaseData, salesData] = await Promise.all([
          axios.get(`${API}/settings/company`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          loadMasterData("purchase"),
          loadMasterData("sales"),
        ]);

        setCompanyType(settingsResponse.data?.company_type || "");
        setCompanyGstNo(settingsResponse.data?.company_gst_no || "");
        setPurchaseMasterData(purchaseData);
        setSalesMasterData(salesData);
      } catch (error) {
        setCompanyType("");
        setCompanyGstNo("");
      }
    };

    loadPageData();
  }, []);

  const purchaseSummary = useMemo(
    () => buildInvoicePayload("purchase", purchaseForm, companyGstNo),
    [purchaseForm, companyGstNo],
  );
  const salesSummary = useMemo(
    () => buildInvoicePayload("sales", salesForm, companyGstNo),
    [salesForm, companyGstNo],
  );

  const clearForm = (type) => {
    if (type === "sales") {
      setSalesForm(emptyForm());
      return;
    }
    setPurchaseForm(emptyForm());
  };

  const applyMatchedParty = (form, setForm, partyName, partyOptions) => {
    const matchedParty = partyOptions.find(
      (party) => (party.name || "").trim().toLowerCase() === partyName.trim().toLowerCase(),
    );

    setForm((prev) => ({
      ...prev,
      party_name: partyName,
      party_gst: matchedParty ? matchedParty.gst_number || "" : prev.party_gst,
      party_address: matchedParty ? matchedParty.address || "" : prev.party_address,
    }));
  };

  const handleSubmit = async (invoiceType) => {
    const isPurchase = invoiceType === "purchase";
    const form = isPurchase ? purchaseForm : salesForm;
    const summary = isPurchase ? purchaseSummary : salesSummary;

    if (!form.invoice_no || !form.party_name) {
      toast.error(`Please fill Invoice No and ${isPurchase ? "Supplier" : "Buyer"} Name`);
      return;
    }

    if (!(form.items || []).some((item) => (item.name || "").trim())) {
      toast.error("Please add at least one item");
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/invoices/manual`,
        {
          invoice_type: invoiceType,
          original_filename: `Manual Entry - ${form.invoice_no}`,
          extracted_data: summary,
          confidence_scores: {},
          status: "verified",
          is_manual_entry: true,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const createdInvoice = response.data?.invoice;
      if (!createdInvoice?.id) {
        throw new Error("Invoice save was not confirmed by the server");
      }

      toast.success(`${isPurchase ? "Purchase" : "Sales"} invoice added successfully!`);
      clearForm(invoiceType);
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || "Failed to save invoice");
      return;
    } finally {
      setSaving(false);
    }

    try {
      const latestMasterData = await loadMasterData(invoiceType);
      if (isPurchase) {
        setPurchaseMasterData(latestMasterData);
      } else {
        setSalesMasterData(latestMasterData);
      }
    } catch (error) {
      toast.warning("Invoice saved, but suggestions could not be refreshed");
    }
  };

  const renderPartySection = ({
    form,
    setForm,
    invoiceType,
    partyOptions,
    accentClasses,
    label,
  }) => (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className={`p-1.5 rounded-md bg-gradient-to-br ${accentClasses.soft}`}>
          <Building size={12} className={accentClasses.icon} />
        </div>
        <h4 className="text-xs font-semibold text-gray-700">{label}</h4>
      </div>

      <div className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-gray-700">
              {invoiceType === "purchase" ? "Supplier Name *" : "Buyer Name *"}
            </Label>
            <div className="relative group">
              <Input
                value={form.party_name}
                onChange={(e) =>
                  applyMatchedParty(form, setForm, e.target.value, partyOptions)
                }
                onBlur={(e) =>
                  applyMatchedParty(form, setForm, e.target.value, partyOptions)
                }
                placeholder={
                  invoiceType === "purchase" ? "Vendor Name" : "Customer Name"
                }
                className={`pl-9 h-8 text-xs border-gray-300 ${accentClasses.border}`}
                list={`${invoiceType}-party-master-list`}
                required
              />
              <div className="absolute left-2.5 top-1/2 -translate-y-1/2">
                <User size={14} className="text-gray-400" />
              </div>
            </div>
            <datalist id={`${invoiceType}-party-master-list`}>
              {partyOptions.map((party) => (
                <option key={`${invoiceType}-${party.name}`} value={party.name || ""} />
              ))}
            </datalist>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-gray-700">GST Number</Label>
            <Input
              value={form.party_gst}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  party_gst: e.target.value.toUpperCase(),
                }))
              }
              placeholder="07AAAAA0000A1Z5"
              maxLength={15}
              className={`h-8 text-xs border-gray-300 ${accentClasses.border}`}
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs font-medium text-gray-700">Address</Label>
          <Input
            value={form.party_address}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, party_address: e.target.value }))
            }
            placeholder="Full address with city, state, and PIN"
            className={`h-8 text-xs border-gray-300 ${accentClasses.border}`}
          />
        </div>
      </div>
    </div>
  );

  const renderAmountSection = ({ summary, accentClasses }) => {
    const breakdownLabel =
      summary.gst_breakdown_type === "igst" ? "IGST" : "CGST + SGST";

    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <div className={`p-1.5 rounded-md bg-gradient-to-br ${accentClasses.soft}`}>
            <Calculator size={12} className={accentClasses.icon} />
          </div>
          <h4 className="text-xs font-semibold text-gray-700">Amount Details</h4>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-gray-700">Basic Amount</Label>
            <Input value={summary.basic_amount || 0} readOnly className="h-8 text-xs bg-gray-50" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-gray-700">GST Amount</Label>
            <Input value={summary.gst || 0} readOnly className="h-8 text-xs bg-gray-50" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-gray-700">Total Amount</Label>
            <Input value={summary.total_amount || 0} readOnly className="h-8 text-xs bg-gray-50 font-semibold" />
          </div>
        </div>

        <div className={`p-3 rounded-lg border ${accentClasses.panel}`}>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
            <div>
              <p className="text-gray-500">Breakdown</p>
              <p className="font-semibold text-gray-800">{breakdownLabel}</p>
            </div>
            <div>
              <p className="text-gray-500">CGST</p>
              <p className="font-semibold text-gray-800">
                {formatCurrency(summary.cgst || 0)}
              </p>
            </div>
            <div>
              <p className="text-gray-500">SGST</p>
              <p className="font-semibold text-gray-800">
                {formatCurrency(summary.sgst || 0)}
              </p>
            </div>
            <div>
              <p className="text-gray-500">IGST</p>
              <p className="font-semibold text-gray-800">
                {formatCurrency(summary.igst || 0)}
              </p>
            </div>
          </div>
          <p className="mt-3 text-[11px] text-gray-600">
            GST split is automatic from the item totals and GST state code comparison.
            If the counterparty GST state matches your company GST state, it uses
            CGST + SGST; otherwise it uses IGST.
          </p>
        </div>

        <div className={`p-3 rounded-lg border ${accentClasses.panel}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-700">Total Invoice Value</p>
              <p className="text-[10px] text-gray-500">Auto-calculated from items</p>
            </div>
            <div className="text-right">
              <p className={`text-sm font-bold ${accentClasses.text}`}>
                Rs {formatCurrency(summary.total_amount || 0)}
              </p>
              <p className="text-[10px] text-gray-500">
                Rs {formatCurrency(summary.basic_amount || 0)} + Rs{" "}
                {formatCurrency(summary.gst || 0)}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderForm = ({
    form,
    setForm,
    invoiceType,
    partyOptions,
    itemOptions,
    summary,
    accentClasses,
  }) => (
    <Card className="border-0 bg-gradient-to-br from-white to-gray-50 shadow-sm overflow-hidden">
      <div className={`p-4 border-b border-gray-100 bg-gradient-to-r ${accentClasses.header}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg bg-gradient-to-br ${accentClasses.badge}`}>
              {invoiceType === "sales" ? (
                <TrendingUp size={16} className="text-white" />
              ) : (
                <ShoppingCart size={16} className="text-white" />
              )}
            </div>
            <div>
              <h3 className="text-sm font-bold text-gray-800">
                {invoiceType === "sales" ? "Sales Invoice" : "Purchase Invoice"}
              </h3>
              <p className="text-xs text-gray-600">
                {invoiceType === "sales"
                  ? "Enter customer invoice details"
                  : "Enter supplier invoice details"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs bg-white">
              {invoiceType === "sales" ? "Sales" : "Purchase"}
            </Badge>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => clearForm(invoiceType)}
              className="h-7 w-7 p-0 text-gray-400 hover:text-red-600"
              title="Clear form"
            >
              <Trash2 size={12} />
            </Button>
          </div>
        </div>
      </div>

      <CardContent className="p-4">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit(invoiceType);
          }}
          className="space-y-4"
        >
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className={`p-1.5 rounded-md bg-gradient-to-br ${accentClasses.soft}`}>
                <FileText size={12} className={accentClasses.icon} />
              </div>
              <h4 className="text-xs font-semibold text-gray-700">Basic Information</h4>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-gray-700">Invoice Number *</Label>
                <div className="relative group">
                  <Input
                    value={form.invoice_no}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, invoice_no: e.target.value }))
                    }
                    placeholder={invoiceType === "sales" ? "INV-001" : "PINV-001"}
                    className={`pl-9 h-8 text-xs border-gray-300 ${accentClasses.border}`}
                    required
                  />
                  <div className="absolute left-2.5 top-1/2 -translate-y-1/2">
                    <Receipt size={14} className="text-gray-400" />
                  </div>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-gray-700">Invoice Date</Label>
                <div className="relative group">
                  <Input
                    type="date"
                    value={form.invoice_date}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, invoice_date: e.target.value }))
                    }
                    className={`pl-9 h-8 text-xs border-gray-300 ${accentClasses.border}`}
                  />
                  <div className="absolute left-2.5 top-1/2 -translate-y-1/2">
                    <Calendar size={14} className="text-gray-400" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {renderPartySection({
            form,
            setForm,
            invoiceType,
            partyOptions,
            accentClasses,
            label: invoiceType === "sales" ? "Buyer Details" : "Supplier Details",
          })}

          <InvoiceItemsEditor
            items={form.items || []}
            onChange={(items) => setForm((prev) => ({ ...prev, items }))}
            companyType={companyType}
            masterItems={itemOptions}
          />

          {renderAmountSection({ summary, accentClasses })}

          <div className="pt-4 border-t border-gray-100">
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/invoices")}
                className="flex-1 h-8 text-xs border-gray-300 hover:border-gray-400 bg-white"
              >
                <ArrowLeft size={12} className="mr-1.5" />
                Back to List
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className={`flex-1 h-8 text-xs bg-gradient-to-r ${accentClasses.badge} text-white shadow-sm`}
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1.5"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={12} className="mr-1.5" />
                    Save {invoiceType === "sales" ? "Sales" : "Purchase"} Invoice
                  </>
                )}
              </Button>
            </div>
          </div>
        </form>
      </CardContent>
    </Card>
  );

  return (
    <Layout>
      <div className="max-w-7xl mx-auto space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-2">
          <div>
            <h1 className="text-lg font-bold bg-gradient-to-r from-primary-500 to-orange-500 bg-clip-text text-transparent">
              Manual Invoice Entry
            </h1>
            <p className="text-gray-500 text-xs mt-0.5">
              Add invoices manually for handwritten or unreadable documents
            </p>
          </div>
          <div className="flex gap-2">
            <Badge className="text-xs py-0.5 px-2.5 bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-sm">
              <Sparkles size={10} className="mr-1" />
              AI Verified
            </Badge>
            <Badge className="text-xs py-0.5 px-2.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-sm">
              <CheckCircle size={10} className="mr-1" />
              Auto GST Split
            </Badge>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid grid-cols-2 bg-gray-100 p-1 rounded-lg">
            <TabsTrigger
              value="sales"
              className="flex items-center gap-2 px-4 py-2 text-xs data-[state=active]:bg-gradient-to-r data-[state=active]:from-green-500 data-[state=active]:to-emerald-500 data-[state=active]:text-white"
            >
              <TrendingUp size={12} />
              Sales Invoice
            </TabsTrigger>
            <TabsTrigger
              value="purchase"
              className="flex items-center gap-2 px-4 py-2 text-xs data-[state=active]:bg-gradient-to-r data-[state=active]:from-red-500 data-[state=active]:to-orange-500 data-[state=active]:text-white"
            >
              <ShoppingCart size={12} />
              Purchase Invoice
            </TabsTrigger>
          </TabsList>

          <TabsContent value="sales" className="mt-4">
            {renderForm({
              form: salesForm,
              setForm: setSalesForm,
              invoiceType: "sales",
              partyOptions: salesMasterData.parties || [],
              itemOptions: salesMasterData.items || [],
              summary: salesSummary,
              accentClasses: {
                soft: "from-green-100 to-emerald-100",
                icon: "text-green-600",
                border: "focus:border-green-500 focus:ring-green-500/20",
                header: "from-green-50 to-emerald-50",
                badge: "from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600",
                panel: "border-green-200 bg-gradient-to-r from-green-50 to-emerald-50",
                text: "text-green-600",
              },
            })}
          </TabsContent>

          <TabsContent value="purchase" className="mt-4">
            {renderForm({
              form: purchaseForm,
              setForm: setPurchaseForm,
              invoiceType: "purchase",
              partyOptions: purchaseMasterData.parties || [],
              itemOptions: purchaseMasterData.items || [],
              summary: purchaseSummary,
              accentClasses: {
                soft: "from-red-100 to-orange-100",
                icon: "text-red-600",
                border: "focus:border-red-500 focus:ring-red-500/20",
                header: "from-red-50 to-orange-50",
                badge: "from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600",
                panel: "border-red-200 bg-gradient-to-r from-red-50 to-orange-50",
                text: "text-red-600",
              },
            })}
          </TabsContent>
        </Tabs>

        <div className="relative overflow-hidden rounded-lg border border-primary-200/50 bg-gradient-to-br from-primary-50/30 to-orange-50/20">
          <div className="p-4 relative">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div className="flex items-center gap-2 p-2 rounded-md bg-white/60 border border-gray-200/50">
                <div className="w-6 h-6 rounded-md flex items-center justify-center bg-gradient-to-br from-green-500 to-emerald-500 text-white text-xs font-bold">
                  G
                </div>
                <p className="text-xs text-gray-700">
                  GST breakup now auto-switches between IGST and CGST/SGST.
                </p>
              </div>
              <div className="flex items-center gap-2 p-2 rounded-md bg-white/60 border border-gray-200/50">
                <div className="w-6 h-6 rounded-md flex items-center justify-center bg-gradient-to-br from-blue-500 to-cyan-500 text-white text-xs font-bold">
                  M
                </div>
                <p className="text-xs text-gray-700">
                  Supplier and item suggestions reuse your existing invoice history.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
